import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import { fetchWithAuth } from '../utils/api';

const Login = () => {
  const navigate = useNavigate();
  const { login, hasRole, user } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });

  const updateSelectedCorp = async (corpId) => {
    try {
      const corp = await fetchWithAuth(`/corps/${corpId}`);
      localStorage.setItem('selectedCorpData', JSON.stringify(corp));
    } catch {}
  };

  useEffect(() => {
    if (user) navigate('/on/landing');
    const msg = localStorage.getItem('loginMessage');
    if (msg) { setError(msg); localStorage.removeItem('loginMessage'); }
  }, [user, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(credentials.username, credentials.password);
      const tokenData = JSON.parse(atob(data.access_token.split('.')[1]));
      if (!hasRole('super') && tokenData.corpid) {
        localStorage.setItem('selectedCorpId', tokenData.corpid);
        await updateSelectedCorp(tokenData.corpid);
      }
      if (hasRole('employee') && tokenData.empid) {
        localStorage.setItem('employeeId', tokenData.empid);
      }
      navigate('/on/landing');
    } catch {
      setError('Login failed. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        {/* Logo mark */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', margin: '0 auto 24px' }}>
          <svg viewBox="0 0 100 100" width="48" height="48">
            <g transform="translate(0, 0)">
              <path d="M 83.8 40.9 A 35 35 0 1 1 59.1 16.2" fill="none" stroke="#061230" strokeWidth="10" strokeLinecap="butt"/>
              <circle cx="74.7" cy="25.3" r="8" fill="#0055FF"/>
              <path d="M 37 65 L 37 45 A 13 13 0 0 1 63 45 L 63 65" fill="none" stroke="#061230" strokeWidth="10" strokeLinecap="butt"/>
            </g>
          </svg>
          <span style={{ fontSize: '28px', fontWeight: '800', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-1px' }}>
            <span style={{ color: '#0055FF' }}>One</span>
            <span style={{ color: '#061230' }}> Nexus</span>
          </span>
        </div>

        <h1 style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: '800', color: '#1e293b' }}>Welcome</h1>
        <p style={{ margin: '0 0 24px', fontSize: '13px', color: '#64748b' }}>Sign in to your account to continue</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>
              Username
            </label>
            <input
              type="text"
              name="username"
              value={credentials.username}
              onChange={handleInputChange}
              placeholder="Enter your username"
              autoComplete="username"
              required
              style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', color: '#1e293b', background: '#fff', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.16s, box-shadow 0.16s', fontFamily: 'inherit' }}
              onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.12)'; }}
              onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          <div className="form-group">
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>
              Password
            </label>
            <input
              type="password"
              name="password"
              value={credentials.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              autoComplete="current-password"
              required
              style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', color: '#1e293b', background: '#fff', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.16s, box-shadow 0.16s', fontFamily: 'inherit' }}
              onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.12)'; }}
              onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '20px', fontSize: '13px' }}>
          <Link to="/on/password-reset?mode=forgot" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}
            onMouseOver={e => e.target.style.textDecoration = 'underline'}
            onMouseOut={e => e.target.style.textDecoration = 'none'}>
            Forgot Password
          </Link>
          <span style={{ color: '#e2e8f0' }}>|</span>
          <Link to="/on/password-reset?mode=reset" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}
            onMouseOver={e => e.target.style.textDecoration = 'underline'}
            onMouseOut={e => e.target.style.textDecoration = 'none'}>
            Reset Password
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
